<script setup>
import { ref, onMounted, computed } from 'vue'; // Import 'computed'
import axios from 'axios';
import { API_BASE_URL } from '@/config';

const departments = ref([]);
// Use column names from dbo.dep schema for form fields
const newDepartment = ref({ dep_no: '', dep_desc: '', Site_no: '' });
const editingDepartment = ref(null); // Holds the department being edited

// Added selectedDepartment ref to track the row clicked in the table for Edit/Delete
const selectedDepartment = ref(null);

// !! IMPORTANT: Replace with the actual base URL of your Node.js backend !!
const departmentsApiUrl = `${API_BASE_URL}/api/departments`;
const sitesApiUrl = `${API_BASE_URL}/api/sites`; // Need to fetch sites for dropdown

const sites = ref([]); // To hold the list of sites for the dropdown

const loading = ref(false); // Add loading state
const error = ref(null); // Add error state
const notification = ref({ // Add notification state
  show: false,
  message: '',
  type: '' // 'success', 'error', 'warning'
});

// --- Computed Properties for v-model ---
// This computed property handles the Department Number input (dep_no)
const currentDepNo = computed({
  get() {
    return editingDepartment.value ? editingDepartment.value.dep_no : newDepartment.value.dep_no;
  },
  set(value) {
    if (editingDepartment.value) {
      editingDepartment.value.dep_no = value;
    } else {
      newDepartment.value.dep_no = value;
    }
  }
});

// This computed property handles the Department Description input (dep_desc)
const currentDepDesc = computed({
  get() {
    return editingDepartment.value ? editingDepartment.value.dep_desc : newDepartment.value.dep_desc;
  },
  set(value) {
    if (editingDepartment.value) {
      editingDepartment.value.dep_desc = value;
    } else {
      newDepartment.value.dep_desc = value;
    }
  }
});

// This computed property handles the Site Number dropdown (Site_no)
const currentSiteNo = computed({
  get() {
    return editingDepartment.value ? editingDepartment.value.Site_no : newDepartment.value.Site_no;
  },
  set(value) {
    if (editingDepartment.value) {
      editingDepartment.value.Site_no = value;
    } else {
      newDepartment.value.Site_no = value;
    }
  }
});
// --- End Computed Properties ---


// Fetch all departments
const fetchDepartments = async () => {
  loading.value = true;
  error.value = null;
  selectedDepartment.value = null; // Deselect any selected department when refreshing
  try {
    const response = await axios.get(departmentsApiUrl);
    departments.value = response.data;
  } catch (err) {
    console.error('Error fetching departments:', err);
    error.value = 'Failed to load departments. Please try again later.';
    showNotification(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

// Fetch sites for the dropdown
const fetchSites = async () => {
     // This fetch is less critical than departments, handle errors internally
    try {
        // Assuming your site API returns data like [{ SiteCode: 'S001', Sitename: 'Site A', ... }]
        // We need SiteCode for the value and Sitename for the display text
        const response = await axios.get(sitesApiUrl);
        sites.value = response.data.map(site => ({
            SiteCode: site.SiteCode, // Use SiteCode as the value
            Sitename: site.Sitename // Use Sitename as the display text
        }));
        if (sites.value.length === 0) {
            console.warn('No sites fetched.');
        }
    } catch (err) {
        console.error('Error fetching sites:', err);
        showNotification('Could not load sites list for dropdown.', 'warning');
    }
};


// Add a new department
const createDepartment = async () => {
  // Use newDepartment.value directly as the form is bound to it when not editing
  if (!newDepartment.value.dep_no.trim() || !newDepartment.value.dep_desc.trim() || !newDepartment.value.Site_no) {
    showNotification('Department Number, Description, and Site are required.', 'warning');
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    const response = await axios.post(departmentsApiUrl, newDepartment.value);
    departments.value.push(response.data);
    newDepartment.value = { dep_no: '', dep_desc: '', Site_no: '' }; // Clear form
    showNotification('Department created successfully!', 'success');
  } catch (err) {
    console.error('Error creating department:', err);
    if (err.response?.status === 409) {
      error.value = err.response.data || 'Department Number already exists.';
    } else if (err.response?.status === 400) {
       error.value = err.response.data || 'Bad Request: Invalid Site Number.';
    }
    else {
      error.value = 'Failed to create department.';
    }
    showNotification(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

// Select a department from the table
const selectDepartment = (department) => {
  selectedDepartment.value = department;
   // If you select a department while editing, stop editing
    if (editingDepartment.value) {
        cancelEditing();
    }
    error.value = null; // Clear previous errors
}


// Start editing
const startEditing = () => {
  if (selectedDepartment.value) {
      editingDepartment.value = { ...selectedDepartment.value };
      // When starting edit, clear newDepartment form and any errors
      newDepartment.value = { dep_no: '', dep_desc: '', Site_no: '' };
      error.value = null;
  } else {
       showNotification('Please select a department to edit.', 'warning');
  }
};

// Cancel editing
const cancelEditing = () => {
  editingDepartment.value = null;
  // When canceling edit, clear newDepartment form and any errors
  newDepartment.value = { dep_no: '', dep_desc: '', Site_no: '' };
  error.value = null;
};

// Update department
const updateDepartment = async () => {
  // Use editingDepartment.value directly as the form is bound to it when editing
  if (!editingDepartment.value.dep_desc.trim() || !editingDepartment.value.Site_no) {
    showNotification('Department Description and Site are required.', 'warning');
    return;
  }

  loading.value = true;
  error.value = null;
  try {
    // PUT request uses dep_no in the URL
    const response = await axios.put(`${departmentsApiUrl}/${editingDepartment.value.dep_no}`, editingDepartment.value);

    const index = departments.value.findIndex(d => d.dep_no === editingDepartment.value.dep_no);
    if (index !== -1) {
      // Update the department in the list with the data from the response
      departments.value[index] = { ...departments.value[index], ...response.data };
    }
     // If the updated department was selected, keep it selected with updated data
     if (selectedDepartment.value && selectedDepartment.value.dep_no === editingDepartment.value.dep_no) {
         selectedDepartment.value = { ...selectedDepartment.value, ...response.data };
     }
    editingDepartment.value = null; // Exit editing mode
    showNotification('Department updated successfully!', 'success');
  } catch (err) {
    console.error('Error updating department:', err);
    if (err.response?.status === 404) {
      error.value = 'Department not found.';
    } else if (err.response?.status === 400) {
        error.value = err.response.data || 'Bad Request: Invalid Site Number during update.';
    }
    else {
      error.value = 'Failed to update department.';
    }
    showNotification(error.value, 'error');
  } finally {
    loading.value = false;
  }
};

// Delete department
const deleteDepartment = async () => {
   if (!selectedDepartment.value) {
       showNotification('Please select a department to delete.', 'warning');
       return;
  }
  const depNoToDelete = selectedDepartment.value.dep_no;

  if (confirm(`Are you sure you want to delete department ${depNoToDelete}? This may also delete related records in other tables (e.g., locations).`)) {
    loading.value = true;
    error.value = null;
    try {
      // DELETE request uses dep_no in the URL
      await axios.delete(`${departmentsApiUrl}/${depNoToDelete}`);
      departments.value = departments.value.filter(d => d.dep_no !== depNoToDelete);
      // If the deleted department was selected or being edited, clear states
      selectedDepartment.value = null;
      if(editingDepartment.value && editingDepartment.value.dep_no === depNoToDelete) {
          editingDepartment.value = null;
      }
      showNotification('Department deleted successfully!', 'success');
    } catch (err) {
      console.error('Error deleting department:', err);
      if (err.response?.status === 404) {
        error.value = 'Department not found.';
      } else if (err.response?.status === 409) {
         // Specific error message from the backend about FK conflicts elsewhere
         error.value = err.response.data || 'Conflict: Cannot delete department because it is referenced by other entities (e.g., locations). You must delete them first.';
      }
      else {
        error.value = 'Failed to delete department.';
      }
      showNotification(error.value, 'error');
    } finally {
      loading.value = false;
    }
  }
};

// Show notification helper function
const showNotification = (message, type) => {
  notification.value.message = message;
  notification.value.type = type;
  notification.value.show = true;
  setTimeout(() => {
    notification.value.show = false;
  }, 5000); // Hide after 5 seconds
};


// Fetch data on mount
onMounted(() => {
    fetchDepartments();
    fetchSites(); // Fetch sites for the dropdown
});
</script>

<template>
  <div class="sites-page">
    <div class="container">
      <div class="form-panel">
        <h2>{{ editingDepartment ? 'Edit Department' : 'Add New Department' }}</h2>
        <form @submit.prevent="editingDepartment ? updateDepartment() : createDepartment()">
          <div class="form-group">
            <label for="dep_no">Department Number:</label>
             <input type="text" id="dep_no" v-model="currentDepNo" required
                   :disabled="editingDepartment !== null || loading"> </div>
          <div class="form-group">
            <label for="dep_desc">Description:</label>
             <input type="text" id="dep_desc" v-model="currentDepDesc" required
                   :disabled="loading"> </div>
           <div class="form-group">
              <label for="site_no">Site:</label>
               <select id="site_no" v-model="currentSiteNo" required :disabled="loading">
                 <option value="" disabled>Select Site</option>
                <option v-for="site in sites" :key="site.SiteCode" :value="site.SiteCode">
                    {{ site.Sitename }} ({{ site.SiteCode }})
                </option>
                 <option value="" v-if="sites.length === 0 && !loading">No sites available</option>
              </select>
            </div>

          <div class="form-actions">
            <button type="submit" class="save-button" :disabled="loading">
              {{ editingDepartment ? 'Update Department' : 'Save Department' }}
            </button>
            <button type="button" class="cancel-button" @click="cancelEditing" v-if="editingDepartment" :disabled="loading">
              Cancel
            </button>
          </div>
        </form>
      </div>

      <div class="table-panel">
        <h2>Department List</h2>

         <div v-if="loading && departments.length === 0">Loading departments...</div>
         <div v-if="error && departments.length === 0" class="error-message">{{ error }}</div>

        <div class="table-actions">
           <button @click="addNew" :disabled="loading">Add New</button>
           <button @click="startEditing" :disabled="!selectedDepartment || loading">Edit</button>
           <button @click="deleteDepartment" :disabled="!selectedDepartment || loading">Delete</button>
           <button @click="fetchDepartments" :disabled="loading">Refresh</button>
         </div>

        <table class="site-table">
          <thead>
            <tr>
              <th>Dept No</th>
              <th>Description</th>
              <th>Site No</th>
              </tr>
          </thead>
          <tbody>
            <tr v-for="department in departments" :key="department.dep_no"
                :class="{ 'selected': selectedDepartment && selectedDepartment.dep_no === department.dep_no }"
                @click="selectDepartment(department)"> <td>{{ department.dep_no }}</td>
              <td>{{ department.dep_desc }}</td>
              <td>{{ department.Site_no }}</td>
              </tr>
             <tr v-if="departments.length === 0 && !loading">
                <td colspan="3" style="text-align: center;">No departments found.</td>
             </tr>
          </tbody>
        </table>
      </div>
    </div>

     <div v-if="notification.show" :class="['notification', notification.type]">
      {{ notification.message }}.
    </div>

  </div>
</template>

<style scoped>
/* Copying the exact styles from your SitesView */
.sites-page {
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background-color: #f4f6f8;
}

.container {
  display: flex;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
  flex-wrap: wrap; /* Allow wrapping on small screens */
}

.form-panel {
  flex: 1; /* Form panel takes 1 part of the available space */
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 300px; /* Ensure panels have a minimum width */
}

.table-panel {
  flex: 2; /* Table panel takes 2 parts of the available space (making it wider) */
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 300px; /* Ensure panels have a minimum width */
  overflow-x: auto; /* Add horizontal scroll if table content is too wide */
}


h2 {
  color: #333;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  color: #555;
  font-weight: bold;
}

input[type="text"],
input[type="number"],
select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

input[type="text"]:focus,
input[type="number"]:focus,
select:focus {
  border-color: #007bff;
  outline: none;
}

input[type="text"]:disabled,
input[type="number"]:disabled,
select:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.form-actions {
  margin-top: 20px;
}

.save-button,
.cancel-button {
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s ease;
}

.save-button {
  background-color: #28a745; /* Green */
  color: white;
  margin-right: 10px;
}

.save-button:hover:not(:disabled) {
  background-color: #218838;
}

.cancel-button {
  background-color: #6c757d; /* Gray */
  color: white;
}

.cancel-button:hover:not(:disabled) {
  background-color: #5a6268;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  opacity: 0.7;
}


.table-actions {
  margin-bottom: 15px;
  display: flex;
  gap: 10px; /* Space between buttons */
  flex-wrap: wrap;
}

.table-actions button {
  padding: 8px 12px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background-color: #007bff; /* Blue */
  color: white;
  cursor: pointer;
  transition: background-color 0.3s ease, opacity 0.3s ease;
}

/* Specific button styles from SitesView */
.table-actions button:nth-child(2) { /* Edit button */
  background-color: #ffc107; /* Yellow */
  border-color: #ffc107;
  color: #212529;
}
.table-actions button:nth-child(2):hover:not(:disabled) {
  background-color: #e0a800;
}

.table-actions button:nth-child(3) { /* Delete button */
  background-color: #dc3545; /* Red */
  border-color: #dc3545;
  color: white;
}
.table-actions button:nth-child(3):hover:not(:disabled) {
  background-color: #c82333;
}


.table-actions button:hover:not(:disabled) {
  background-color: #0056b3;
  border-color: #0056b3;
}


.site-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 0.9rem;
}

/* Increased padding for bigger cells */
.site-table th,
.site-table td {
  padding: 12px; /* Increased from 10px */
  border: 1px solid #ddd;
  text-align: left;
}

.site-table th {
  background-color: #e9ecef; /* Light gray */
  font-weight: bold;
  color: #495057;
}

.site-table tbody tr:hover {
  background-color: #f8f9fa; /* Lighter gray on hover */
  cursor: pointer;
}

.site-table tbody tr.selected {
  background-color: #cfe2ff; /* Light blue when selected */
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .container {
    flex-direction: column;
  }

  .form-panel,
  .table-panel {
    margin-bottom: 20px;
    min-width: unset; /* Remove min-width on small screens */
    flex: unset; /* Remove flex on small screens */
    width: 100%; /* Make panels full width */
  }

  .table-actions {
    flex-direction: column; /* Stack buttons vertically */
    gap: 5px;
  }

  .table-actions button {
    width: 100%; /* Make buttons full width */
    margin-right: 0;
  }
}

/* Notification Styling */
.notification {
  position: fixed;
  bottom: 20px;
  left: 20px;
  padding: 15px 20px;
  border-radius: 4px;
  color: white;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transition: opacity 0.5s ease-in-out;
}

.notification.success {
  background-color: #28a745; /* Green */
}

.notification.error {
  background-color: #dc3545; /* Red */
}

.notification.warning {
  background-color: #ffc107; /* Yellow */
  color: #212529;
}

.error-message {
  color: #dc3545;
  margin-bottom: 15px;
  padding: 10px;
  border: 1px solid #dc3545;
  background-color: #fff3f3;
  border-radius: 4px;
}

</style>
